import { ActionIcon, Tooltip } from "@mantine/core";
import {
  IconFileText,
  IconMessageCircle,
  IconMicrophone,
  IconMicrophoneOff,
  IconX,
} from "@tabler/icons-react";
import DockStatusChip from "./DockStatusChip";
import useSpeechBox, { useSpeechScroll } from "./useSpeechBox";
import { STATE_TITLES } from "./voiceStateLabels";
import css from "./AssistantDock.module.scss";

// Mantine dropdowns and modals opened from the review card must clear the dock
// (z-index 1000). 1200 is the value the widgets already receive.
const PORTAL_Z_INDEX = 1200;

// One utterance at a time. Whoever holds the floor gets the whole speech area:
// showing the user's question above the assistant's answer read as a chat log
// squeezed into a bar, and both halves ended up truncated.
const getSpeech = ({
  voiceState,
  errorMessage,
  statusLabel,
  assistantText,
  userTranscript,
}) => {
  if (errorMessage) return { text: errorMessage, tone: "error" };
  if (voiceState === "error")
    return { text: STATE_TITLES.error, tone: "error" };
  if (voiceState === "thinking" && statusLabel)
    return { text: statusLabel, tone: "progress" };
  // The hook clears one field when the other starts, so at most one is live;
  // between turns this holds whichever spoke last.
  if (assistantText) return { text: assistantText, tone: "assistant" };
  if (userTranscript) return { text: userTranscript, tone: "user" };
  return { text: "", tone: "assistant" };
};

const DockBarFace = ({
  hidden,
  voiceState,
  userTranscript,
  assistantText,
  statusLabel,
  errorMessage,
  isMicMuted,
  hasPendingReview,
  onSpeechLinesChange,
  onOpenReview,
  onToggleMute,
  onOpenChat,
  onClose,
}) => {
  const speech = getSpeech({
    voiceState,
    errorMessage,
    statusLabel,
    assistantText,
    userTranscript,
  });
  const { viewportRef, contentRef } = useSpeechBox(
    speech.text,
    onSpeechLinesChange,
  );

  useSpeechScroll({
    viewportRef,
    text: speech.text,
    // Only a live transcript chases its own tail; everything else is read from
    // the start, at the reader's pace.
    pinToBottom: speech.tone === "user",
  });

  return (
    <div className={css.barFace} aria-hidden={hidden}>
      <DockStatusChip voiceState={voiceState} />

      <div
        className={css.speechViewport}
        ref={viewportRef}
        data-tone={speech.tone}
      >
        <p className={css.speechText} ref={contentRef} aria-live="polite">
          {speech.text}
        </p>
      </div>

      <div className={css.controls}>
        {hasPendingReview ? (
          <Tooltip label="Review draft" zIndex={PORTAL_Z_INDEX} withinPortal>
            <ActionIcon
              aria-label="Review draft"
              size={30}
              radius="xl"
              variant="subtle"
              className={css.controlButton}
              onClick={onOpenReview}
              tabIndex={hidden ? -1 : 0}
            >
              <IconFileText size={17} />
              <span className={css.controlBadge} aria-hidden="true" />
            </ActionIcon>
          </Tooltip>
        ) : null}

        <Tooltip
          zIndex={PORTAL_Z_INDEX}
          withinPortal
          label={isMicMuted ? "Unmute microphone" : "Mute microphone"}
        >
          <ActionIcon
            aria-label={isMicMuted ? "Unmute microphone" : "Mute microphone"}
            size={30}
            radius="xl"
            variant={isMicMuted ? "filled" : "subtle"}
            color={isMicMuted ? "red" : undefined}
            className={css.controlButton}
            onClick={onToggleMute}
            disabled={voiceState === "error" || voiceState === "connecting"}
            tabIndex={hidden ? -1 : 0}
          >
            {isMicMuted ? (
              <IconMicrophoneOff size={17} />
            ) : (
              <IconMicrophone size={17} />
            )}
          </ActionIcon>
        </Tooltip>

        <Tooltip label="Open in chat" zIndex={PORTAL_Z_INDEX} withinPortal>
          <ActionIcon
            aria-label="Open in chat"
            size={30}
            radius="xl"
            variant="subtle"
            className={css.controlButton}
            onClick={onOpenChat}
            tabIndex={hidden ? -1 : 0}
          >
            <IconMessageCircle size={17} />
          </ActionIcon>
        </Tooltip>

        <ActionIcon
          aria-label="Close voice mode"
          size={30}
          radius="xl"
          variant="subtle"
          className={css.controlButton}
          onClick={onClose}
          tabIndex={hidden ? -1 : 0}
        >
          <IconX size={17} />
        </ActionIcon>
      </div>
    </div>
  );
};

export { getSpeech };
export default DockBarFace;
